import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useNavigate } from 'react-router-dom';
import type { ContentPageParsed } from '../../../@types/ContentPage';
import { all as getBrandings } from '../../../actions/branding';
import { get as getContentPage } from '../../../actions/contentpage';
import { clear, facets as getFacets, search } from '../../../actions/search';
import DefaultFilterBlock from '../../../components/filters/default-filter-block';
import TypeFilterBar from '../../../components/filters/type-filter-bar';
import HeroShell from '../../../components/hero/hero-shell';
import { Pagination, PlaceholderResultSet, ResultSet } from '../../../components/search';
import { events, QueryFilter, text } from '../../../helpers';
import { asHtml } from '../../../helpers/markdown';
import useTrackPageView from '../../../hooks/useTrackPageView';
import type { RootState } from '../../../reducers';
import { getLandingPage, getShouldHideAds } from '../../../selectors/content-page';
import { getLandingPageQueryFilter } from '../../../selectors/content-page';
import { requestStatus } from '../../../shared/statuses';
import Content from '../../../styled-components/components/Content';
import { H1 } from '../../../styled-components/components/Heading';
import Hero from '../../../styled-components/components/Hero';
import Loader from '../../../styled-components/components/Loader';
import {
  getFacetList,
  getSearchPages,
  getSearchResultSet,
  getSearchSorting,
  shouldFetchBrandings as shouldFetchBrandingsSelector,
} from '../../../utils/reselectors';

interface LandingPageProps {
  slug: string;
}

const LandingPage = ({ slug }: LandingPageProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const page = useSelector<RootState, ContentPageParsed | null>((s) => getLandingPage(s, slug));
  const shouldHideAds = useSelector<RootState, boolean>((s) => getShouldHideAds(s, slug));
  const isSearching = useSelector<RootState, boolean>(
    (s) => s.search.requestStatus === requestStatus.FETCHING_LIST,
  );
  const shouldFetchBrandings = useSelector(shouldFetchBrandingsSelector);
  const queryFilter = useSelector<RootState, string | null>((s) =>
    getLandingPageQueryFilter(s, slug),
  );
  const resultSet = useSelector(getSearchResultSet);
  const sortOptions = useSelector(getSearchSorting);
  const pages = useSelector(getSearchPages);
  const facets = useSelector(getFacetList);
  const trackPageViewEvent = useTrackPageView();

  const actions = bindActionCreators(
    { getContentPage, getBrandings, getFacets, search, clear },
    dispatch,
  );

  const scrollRefTarget = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    actions.search(new QueryFilter(queryFilter));

    if (facets.length < 2) actions.getFacets();

    if (shouldFetchBrandings) actions.getBrandings();

    document.title = text.createPageTitle(page?.title);
    trackPageViewEvent();
    setTimeout(events.resetViewport, 10);
  }, []);

  const shouldScrollView = () => {
    const scrollTarget =
      scrollRefTarget && scrollRefTarget.current
        ? scrollRefTarget.current.getBoundingClientRect()
        : null;
    if (scrollTarget && scrollTarget.top < 0 && document.body && document.documentElement) {
      document.body.scrollTop = document.documentElement.scrollTop =
        scrollTarget.top + document.documentElement.scrollTop;
    }
  };

  const onApplyFilter = (filterQuery: string) => {
    shouldScrollView();
    actions.search(new QueryFilter(filterQuery));
  };

  const onNavigate = (qf: QueryFilter) => {
    if (!new QueryFilter(queryFilter).isSame(qf)) {
      navigate(`/sok${qf.toQueryURL()}`);
    }
  };

  const onRequestSearch = (qf: QueryFilter) => {
    qf = qf.removePagination();
    onNavigate(qf);
    actions.search(qf);
  };

  if (!page || facets.length < 2) {
    return (
      <div className="m-t">
        <Loader />
      </div>
    );
  }

  let resultSetMarkup = (
    <>
      <section className="page-section" ref={scrollRefTarget}>
        <ResultSet data={resultSet} title={page.title} shouldHideAds={shouldHideAds} />
      </section>
      <section className="page-section">
        <Pagination pages={pages} onApplyPage={onApplyFilter} />
      </section>
    </>
  );
  if (isSearching) {
    resultSetMarkup = <PlaceholderResultSet size={12} />;
  }

  let contentSecondary = null;
  if (page.bottomContent) {
    contentSecondary = (
      <section className="page-section">
        <div className="container border-top">
          <div className="row">
            <div className="col-md-8">
              <div
                data-am-content="text-smaller"
                dangerouslySetInnerHTML={{ __html: asHtml(page.bottomContent) }}
              />
            </div>
          </div>
        </div>
      </section>
    );
  }

  let content;
  if (page.content) {
    content = <div data-am-content="" dangerouslySetInnerHTML={{ __html: asHtml(page.content) }} />;
  }

  if (page.smallFeaturedMedia && page.featuredMedia) {
    return (
      <>
        <DefaultFilterBlock
          target="/sok"
          silent={true}
          umbrellaFacet={page.umbrellaSearchTag || undefined}
          useContainer={true}
        >
          <Hero
            image={{
              src: page.featuredMedia,
              srcLtSm: page.smallFeaturedMedia || undefined,
              alt: page.title,
            }}
          >
            {page.title && <H1>{page.title}</H1>}
            {content && <Content>{content}</Content>}
          </Hero>
        </DefaultFilterBlock>
        <div data-wx-page="">
          <div className="container">
            <TypeFilterBar
              onRequestSearch={onRequestSearch}
              sortOptions={sortOptions}
              onApplySort={onApplyFilter}
            />
            {resultSetMarkup}
            {contentSecondary}
          </div>
        </div>
      </>
    );
  } else {
    return (
      <HeroShell>
        <DefaultFilterBlock
          target="/sok"
          silent={true}
          umbrellaFacet={page.umbrellaSearchTag || undefined}
        >
          <div className="p-t hidden-md" />
          <H1>{page.title}</H1>
          {content && (
            <div className="row m-b">
              <div className="col-md-10 col-lg-8">{content}</div>
            </div>
          )}
        </DefaultFilterBlock>
        {resultSetMarkup}
        {contentSecondary}
      </HeroShell>
    );
  }
};

export default LandingPage;
